import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { api } from "../../api/client";
import { formatMoney } from "../../lib/money";
import { useAuth } from "../auth/useAuth";
import { CompanyDialog } from "./CompanyDialog";
import { CompanyDeleteDialog } from "./CompanyDeleteDialog";
import { CompanyContacts } from "./CompanyContacts";
import type { Company } from "../../api/types";

// 客戶。**統一編號是唯一識別鍵,公司名稱不是** —— 同一家公司會出現
// 「BSC(波士頓)」這類簡稱與中英文混用,拿名稱比對必然出錯。
// 統編可留空(個人客戶),但只要有值就必須唯一,重複時後端回 409。
//
// 建檔與編輯都在懸浮視窗裡做(業主原話:「一個按鈕、跳出懸浮視窗」)。
// 舊版是常駐在清單上方的兩欄內嵌表單,七個欄位根本擺不下,而且每次進這一頁
// 都先看到一份空表單 —— 這一頁大多數時候是拿來查的,不是拿來建的。

/** 表格欄位。順序與對話框一致,看到什麼就是填了什麼。
 *
 * 頭尾兩欄沒有標題(展開箭頭、停用按鈕),所以用 `id` 當 key ——
 * 兩個空字串當 key 會撞在一起,React 會把那兩欄認成同一個。 */
const HEADERS: { id: string; label: string }[] = [
  { id: "expand", label: "" },
  { id: "name", label: "公司名稱" },
  { id: "tax", label: "統一編號" },
  { id: "contact", label: "窗口、負責人" },
  { id: "mobile", label: "手機電話" },
  { id: "phone", label: "市內電話" },
  { id: "address", label: "聯絡地址" },
  { id: "actions", label: "" },
];

/** 應收兩欄(2026-09-30,財務整理 4)。**只有老闆** —— 客戶頁財務本來就進不來。 */
const MONEY_HEADERS: { id: string; label: string }[] = [
  { id: "receivable", label: "未收" },
  { id: "overdue", label: "最久欠" },
];

export function CompanyList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const showMoney = user?.role === "OWNER" || user?.role === "FINANCE";
  const headers = showMoney ? [...HEADERS.slice(0, -1), ...MONEY_HEADERS, HEADERS[HEADERS.length - 1]] : HEADERS;
  const [keyword, setKeyword] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  // null 代表新增;有值代表編輯那一筆。
  const [editing, setEditing] = useState<Company | null>(null);
  // 展開來看窗口的那一筆。一次只開一個 —— 全部攤開的話這一頁會長到看不完。
  const [openId, setOpenId] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  // 正在確認要刪掉的那一家。**真的刪**,不是停用 —— 見 CompanyDeleteDialog。
  const [deleting, setDeleting] = useState<Company | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["companies", keyword, showInactive, showMoney],
    queryFn: () =>
      api.get<Company[]>("/companies", {
        q: keyword,
        include_inactive: showInactive,
        // 客戶清單也是開單時的客戶下拉;只有這一頁、只有老闆才請後端把帳算一遍。
        ...(showMoney ? { receivables: true } : {}),
      }),
  });

  const companies = Array.isArray(data) ? data : [];

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["companies"] });

  // **停用不是刪除。** 業主 2026-09-18 決定:客戶真的刪掉的話,
  // 舊報價單上的客戶名稱會跟著壞 —— 歷史單據不可以因為今天的整理而改變。
  const deactivate = useMutation({
    mutationFn: (id: number) => api.del(`/companies/${id}`),
    onSuccess: refresh,
  });
  const restore = useMutation({
    mutationFn: (id: number) => api.post(`/companies/${id}/restore`),
    onSuccess: refresh,
  });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (company: Company) => {
    setEditing(company);
    setDialogOpen(true);
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-xl font-semibold text-[var(--text)]">客戶</h2>
        <Button
          type="button"
          variant="primary"
          onClick={openCreate}
          icon={<Plus aria-hidden="true" size={16} />}
        >
          新增客戶
        </Button>
      </div>
      <p className="m-0 text-sm text-[var(--text-2)]">
        點公司名稱可以修改這一筆。統一編號就是業主口中的「行號」,有填就必須是唯一的。
      </p>

      <div className="flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5">
        <Search aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
        <input
          type="search"
          value={keyword}
          aria-label="搜尋客戶"
          placeholder="搜尋公司名稱或統一編號"
          onChange={(event) => setKeyword(event.target.value)}
          className="h-9 w-full min-w-0 border-0 bg-transparent text-sm text-[var(--text)] placeholder:text-[var(--text-3)]"
        />
      </div>

      {/* 停用的客戶預設看不到,但**要看得到才復原得了** ——
          停用的意義有一半在「按錯了救得回來」。 */}
      <label className="flex items-center gap-2 text-sm text-[var(--text-2)]">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(event) => setShowInactive(event.target.checked)}
        />
        顯示已停用的客戶
      </label>

      {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          客戶清單載入失敗,請稍後再試。
        </p>
      ) : null}

      {companies.length > 0 ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="客戶清單" className="w-full min-w-[42rem] border-collapse text-left">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                {headers.map((header) => (
                  <th key={header.id} scope="col" className="px-3 py-2">
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => (
                <Fragment key={company.id}>
                  <tr className="border-t border-[var(--border)]">
                    <td className="py-2 pl-2 pr-0">
                      {/* 展開來看這家公司的窗口。**箭頭自己是一個按鈕** ——
                          點公司名還是編輯,兩件事不要搶同一個點擊。 */}
                      <button
                        type="button"
                        aria-expanded={openId === company.id}
                        aria-label={`${company.name} 的窗口`}
                        onClick={() => setOpenId(openId === company.id ? null : company.id)}
                        className="rounded-[var(--radius)] p-1 text-[var(--text-2)] hover:bg-[var(--surface-2)]"
                      >
                        {openId === company.id ? (
                          <ChevronDown size={16} aria-hidden="true" />
                        ) : (
                          <ChevronRight size={16} aria-hidden="true" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-sm text-[var(--text)]">
                      {/* 用按鈕而不是整列掛 onClick:整列點得開的話,鍵盤使用者
                          完全按不到,焦點也停不上去。 */}
                      <button
                        type="button"
                        onClick={() => openEdit(company)}
                        className="rounded-[var(--radius)] text-left text-[var(--accent)] underline-offset-2 hover:underline"
                      >
                        {company.name}
                      </button>
                      {!company.is_active ? (
                        <span className="ml-2 rounded-[var(--radius)] bg-[var(--surface-2)] px-1.5 py-0.5 text-xs text-[var(--text-3)]">
                          已停用
                        </span>
                      ) : null}
                    </td>
                    <td className="tabular px-3 py-2 text-sm text-[var(--text-2)]">{company.tax_id}</td>
                    <td className="px-3 py-2 text-sm text-[var(--text-2)]">{company.contact_name}</td>
                    <td className="tabular px-3 py-2 text-sm text-[var(--text-2)]">{company.contact_mobile}</td>
                    <td className="tabular px-3 py-2 text-sm text-[var(--text-2)]">{company.contact_phone}</td>
                    <td className="px-3 py-2 text-sm text-[var(--text-2)]">{company.address}</td>
                    {showMoney ? (
                      <>
                        <td className="tabular px-3 py-2 text-right text-sm text-[var(--text)]">
                          {company.receivable && company.receivable !== "0.00" ? formatMoney(company.receivable) : "—"}
                        </td>
                        <td
                          className={
                            "tabular px-3 py-2 text-sm " +
                            (company.oldest_overdue_days != null && company.oldest_overdue_days > 30
                              ? "text-[var(--danger)]"
                              : "text-[var(--text-2)]")
                          }
                        >
                          {company.oldest_overdue_days != null ? `${company.oldest_overdue_days} 天` : ""}
                        </td>
                      </>
                    ) : null}
                    <td className="px-2 py-2 text-right">
                      {company.is_active ? (
                        <Button
                          type="button"
                          onClick={() => {
                            // **講清楚會發生什麼、以及救不救得回來。**
                            // 業主原話:「應該是出現確定是否要讓我刪除 這個無法挽回」
                            const ok = window.confirm(
                              `要停用客戶「${company.name}」嗎?\n\n` +
                                "停用之後:開新單選不到它、清單裡看不到它。\n" +
                                "已經開好的報價單不受影響,客戶名稱照樣印得出來。\n\n" +
                                "這個動作可以復原 —— 勾「顯示已停用」就找得回來。",
                            );
                            if (ok) deactivate.mutate(company.id);
                          }}
                          icon={<Trash2 size={15} aria-hidden="true" />}
                        >
                          停用
                        </Button>
                      ) : (
                        // **刪除只出現在已停用那一列**(業主 2026-09-20 選的
                        // 「停用後才可以刪除」)。兩段式:手滑刪不掉還在用的客戶。
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            onClick={() => restore.mutate(company.id)}
                            icon={<RotateCcw size={15} aria-hidden="true" />}
                          >
                            復原
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            onClick={() => setDeleting(company)}
                            icon={<Trash2 size={15} aria-hidden="true" />}
                          >
                            刪除
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {openId === company.id ? (
                    <tr className="border-t border-[var(--border)]">
                      <td colSpan={headers.length} className="p-2">
                        <CompanyContacts companyId={company.id} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* 空狀態分兩種,講的不是同一件事:整本通訊錄是空的,還是「這個關鍵字查不到」。
          後者放兔子只會讓人以為資料不見了,所以只有前者才進 EmptyState。 */}
      {!isLoading && !error && companies.length === 0 ? (
        keyword.trim() === "" ? (
          <EmptyState
            title="還沒有客戶資料。"
            description="用右上角的「新增客戶」建第一筆。統一編號就是行號,有填就必須是唯一的。"
          />
        ) : (
          <p className="m-0 text-sm text-[var(--text-3)]">沒有符合的客戶。</p>
        )
      ) : null}

      <CompanyDialog open={dialogOpen} onClose={() => setDialogOpen(false)} company={editing} />
      <CompanyDeleteDialog company={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}
